import {Plural, Trans} from '@lingui/macro'
import type {ReactNode} from 'react'

import {PlanTypeName} from '~/api/types/billing'
import {dayjs} from '~/dates/dayjs'
import type {PricingPlan} from '~/entitlement/use-pricing-plan'
import {PageBanner} from '~/generic-ui/page-banner'
import tracking from '~/tracking'

import type {TrialActivationBannerActionedExtended} from './types'

import useConditionedPlans from '../plan-pill/use-conditioned-plans'

// NOTE: prop `userDidSelectPremiumPlan` can be removed end July 2023 when all trials using this value expire
interface TrialBannerProps {
    pricingPlan: PricingPlan
    trialEndDate?: string
    isBeforeTrial?: boolean
    isDuringTrial?: boolean
    userDidSelectPremiumPlan?: boolean
    handleCta: (modal: 'choose-plans' | 'how-trial-works') => void
}

export const TrialBanner = ({
    pricingPlan,
    trialEndDate,
    isBeforeTrial,
    isDuringTrial,
    handleCta,
    userDidSelectPremiumPlan,
}: TrialBannerProps) => {
    const {essential} = useConditionedPlans()
    if (isBeforeTrial && ['free', 'starter'].includes(pricingPlan)) {
        return (
            <Banner>
                <Trans>
                    Save even more bookkeeping time with a free trial of Pleo premium features.
                </Trans>{' '}
                <PageBanner.Link
                    role="button"
                    onClick={() => {
                        tracking.trialActivationBannerActioned({
                            action: 'clicked',
                            plan: 'free',
                        } as TrialActivationBannerActionedExtended)
                        handleCta('how-trial-works')
                    }}
                >
                    <Trans>Learn more</Trans>
                </PageBanner.Link>
            </Banner>
        )
    }
    if (isBeforeTrial && ['pro', 'essential'].includes(pricingPlan)) {
        return (
            <Banner>
                <Trans>
                    Start your free trial of Pleo {PlanTypeName[essential.type]} features by adding
                    money to your Wallet.
                </Trans>{' '}
                <PageBanner.Link
                    role="button"
                    onClick={() => {
                        tracking.trialActivationBannerActioned({
                            action: 'clicked',
                            plan: 'paid',
                        } as TrialActivationBannerActionedExtended)
                        handleCta('how-trial-works')
                    }}
                >
                    <Trans>Learn more</Trans>
                </PageBanner.Link>
            </Banner>
        )
    }
    if (
        isDuringTrial &&
        ['free', 'starter'].includes(pricingPlan) &&
        trialEndDate &&
        !userDidSelectPremiumPlan
    ) {
        return (
            <Banner>
                <Trans>
                    Your trial ends on {dayjs(trialEndDate).format('LL')}. Save even more time with
                    our premium features.
                </Trans>{' '}
                <PageBanner.Link
                    role="button"
                    onClick={() => {
                        tracking.trialActivationBannerActioned({
                            action: 'plans_clicked',
                        } as TrialActivationBannerActionedExtended)
                        handleCta('choose-plans')
                    }}
                >
                    <Trans>Compare plans</Trans>
                </PageBanner.Link>
            </Banner>
        )
    }
    if (
        isDuringTrial &&
        (['pro', 'essential'].includes(pricingPlan) || userDidSelectPremiumPlan) &&
        trialEndDate
    ) {
        const remainingTrialDays = Number(dayjs(trialEndDate).diff(dayjs(), 'days')) + 1
        return (
            <Banner>
                {remainingTrialDays > 1 ? (
                    <Trans>
                        Your trial ends in{' '}
                        <Plural value={remainingTrialDays} one="# day" other="# days" />
                    </Trans>
                ) : (
                    <Trans>It is the last day of your trial</Trans>
                )}
            </Banner>
        )
    }
    return null
}

const Banner = ({children}: {children: ReactNode}) => (
    <PageBanner data-testid="trial-banner">
        <PageBanner.Text align="center">{children}</PageBanner.Text>
    </PageBanner>
)
